import { Box } from '@mui/material';
import { useState, useEffect } from 'react';
import { CarouselContent } from './carousel-content';
import LoadingSlide from './components/loading-slide';
import { TCarouselProps } from './types';
import { CarouselEmblaFullscreen } from '../carousel-embla-fullscreen';
import { useIsMobile } from '../../hooks';
import { isVideo, isVideoBlob } from '../../utils';

export function CarouselEmbla({
  height,
  variant,
  urls,
  mainImageUrl,
  withFullscreen = false,
  thumbnailsButtons = true,
  isPreview,
  sx,
  onFullScreenClick,
  fullscreenOnImageClick = false,
  onSendAnalyticsEvent,
  hasBlobs = false,
}: TCarouselProps) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const [slides, setSlides] = useState<Array<{ url: string; isVideo: boolean }>>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const processUrls = async (urlsArray: (string | Blob)[], hasBlobs: boolean) => {
    if (!urlsArray.length) {
      setSlides([]);
      return;
    }

    setIsProcessing(true);

    try {
      if (!hasBlobs) {
        // Synchronous processing for regular URLs
        const processedSlides = urlsArray.map((url) => {
          const urlString = typeof url === 'string' ? url : URL.createObjectURL(url);
          return {
            url: urlString,
            isVideo: isVideo(url),
          };
        });
        setSlides(processedSlides);
      } else {
        // Asynchronous processing for blob: URLs and mixed content
        const processedSlides = await Promise.all(
          urlsArray.map(async (url) => {
            const urlString = typeof url === 'string' ? url : URL.createObjectURL(url);
            const videoDetectionResult = isVideoBlob(url);
            const isVideoResult =
              videoDetectionResult instanceof Promise
                ? await videoDetectionResult
                : videoDetectionResult;

            return {
              url: urlString,
              isVideo: isVideoResult,
            };
          }),
        );
        setSlides(processedSlides);
      }
    } catch (error) {
      console.error('Error processing URLs for video detection:', error);
      // Fallback: create slides without video detection
      const fallbackSlides = urlsArray.map((url) => ({
        url: typeof url === 'string' ? url : URL.createObjectURL(url),
        isVideo: false,
      }));
      setSlides(fallbackSlides);
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    processUrls(urls, hasBlobs);
  }, [urls, hasBlobs]);

  return (
    <>
      <Box
        id="carousel-main"
        sx={{
          // use the provided height on mobile
          // use 16:9 aspect ratio on desktop
          ...(isMobile
            ? { height }
            : {
                aspectRatio: '16 / 9',
                '@supports not (aspect-ratio: 16 / 9)': {
                  '&::before': {
                    content: '""',
                    display: 'block',
                    paddingTop: 'calc(9 / 16 * 100%)',
                  },
                  '& > *': {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                  },
                },
              }),
          overflow: 'hidden',
          position: 'relative',
          ...sx,
        }}
      >
        {!isMobile && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: 1,
              height: '100%',
              width: '100%',
              cursor: fullscreenOnImageClick ? 'pointer' : 'unset',
              background:
                'linear-gradient(0deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.1) 40%, transparent 60%), linear-gradient(180deg, rgba(0, 0, 0, 0) 51.26%, rgba(0, 0, 0, 0.5) 100%)',
            }}
            onClick={() => {
              if (fullscreenOnImageClick && onFullScreenClick) onFullScreenClick();
            }}
          />
        )}

        {!urls.length || isProcessing || !slides.length ? (
          <LoadingSlide>
            <></>
          </LoadingSlide>
        ) : (
          <>
            <CarouselContent
              slides={slides}
              mainImageUrl={mainImageUrl}
              variant={variant}
              withFullscreen={withFullscreen}
              onFullScreenClick={onFullScreenClick}
              fullscreenOnImageClick={fullscreenOnImageClick}
              onOpenFullscreen={() => {
                setOpen(true);
                onSendAnalyticsEvent?.();
              }}
              thumbnailsButtons={thumbnailsButtons}
            />
          </>
        )}
      </Box>

      {withFullscreen && (
        <CarouselEmblaFullscreen
          open={open}
          onClose={() => setOpen(false)}
          urls={urls}
          isPreview={isPreview}
        />
      )}
    </>
  );
}
